import { useMemo, useState } from "react";
import {
  Grid3x3,
  X,
  CalendarDays,
  Clock,
  Layers,
  Activity,
} from "lucide-react";
import { useGetLogbookAnalysisSheetQuery } from "../api/logbookApi";

type Cell = { days: number; hours: number };
type SubjectRow = {
  id: number | null;
  code: string;
  description: string;
  cells: Record<string, Cell>;
  total_days: number;
  total_hours: number;
};
type Metric = "days" | "hours";

const fmt = (n: number) =>
  Number.isInteger(n) ? String(n) : n.toFixed(1);

/**
 * Coverage matrix for the assessor: training-code subjects down the side,
 * the logbook's weeks (1..size) across the top. Rendered as a heat-mapped,
 * responsive table inside a modal. The cell metric can be toggled between
 * days logged and hours logged.
 */
export default function LogbookAnalysisSheet({
  logbookId,
}: {
  logbookId: string;
}) {
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState<Metric>("days");

  const { data, isLoading, isError } = useGetLogbookAnalysisSheetQuery(
    { logbook_id: logbookId },
    { skip: !logbookId || !open }
  );

  const weeks: number[] = data?.weeks ?? [];
  const subjects: SubjectRow[] = data?.subjects ?? [];
  const columnTotals: Record<string, Cell> = data?.column_totals ?? {};
  const grand: Cell = data?.grand_total ?? { days: 0, hours: 0 };

  const metricOf = (c?: Cell) =>
    !c ? 0 : metric === "days" ? c.days : c.hours;

  // Peak cell value for the current metric — drives the heat scale.
  const maxCell = useMemo(() => {
    let m = 0;
    for (const s of subjects) {
      for (const w of weeks) {
        m = Math.max(m, metricOf(s.cells?.[String(w)]));
      }
    }
    return Math.max(m, 1);
  }, [subjects, weeks, metric]);

  const weeksActive = useMemo(
    () => weeks.filter((w) => (columnTotals?.[String(w)]?.days ?? 0) > 0).length,
    [weeks, columnTotals]
  );

  const totalOf = (s: SubjectRow) =>
    metric === "days" ? s.total_days : s.total_hours;

  // Peak per-subject total — drives the (separate) heat scale on the Total column.
  const maxTotal = useMemo(
    () => Math.max(1, ...subjects.map(totalOf)),
    [subjects, metric]
  );

  // Heat colour: transparent→indigo ramp, keyed off the metric intensity.
  const heat = (v: number) => {
    if (v <= 0) return { background: "transparent", color: "#cbd5e1" };
    const t = v / maxCell;
    const alpha = 0.14 + 0.82 * t;
    return {
      background: `rgba(79, 70, 229, ${alpha.toFixed(3)})`,
      color: alpha > 0.55 ? "#ffffff" : "#3730a3",
    };
  };

  // Row-total heat: a warmer amber ramp so the Total column reads distinctly
  // from the indigo week cells.
  const heatTotal = (v: number) => {
    if (v <= 0) return { background: "#eef2ff", color: "#6366f1" };
    const t = v / maxTotal;
    const alpha = 0.16 + 0.8 * t;
    return {
      background: `rgba(217, 119, 6, ${alpha.toFixed(3)})`,
      color: alpha > 0.5 ? "#ffffff" : "#92400e",
    };
  };

  const legendStops = [0, 0.25, 0.5, 0.75, 1];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white hover:border-indigo-300 hover:shadow-sm transition text-left"
      >
        <span className="flex items-center gap-2 font-bold text-sm text-indigo-900">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600">
            <Grid3x3 size={16} />
          </span>
          Analysis Sheet
          <span className="hidden sm:inline text-xs font-normal text-gray-400">
            subjects × weeks coverage heat-map
          </span>
        </span>
        <span className="text-xs font-semibold text-indigo-600">Open →</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-6xl sm:rounded-2xl shadow-xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b">
              <div className="flex items-center gap-2 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-600 text-white shrink-0">
                  <Grid3x3 size={16} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-black text-indigo-900 leading-tight truncate">
                    Analysis Sheet
                  </h3>
                  <p className="text-[11px] text-gray-400 leading-tight">
                    Subjects (rows) × weeks (columns)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Metric toggle */}
                <div className="flex rounded-lg border overflow-hidden text-xs font-semibold">
                  {(["days", "hours"] as Metric[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setMetric(m)}
                      className={`px-3 py-1.5 capitalize ${
                        metric === m
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="grid place-items-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 sm:px-5 py-3 border-b bg-gray-50">
              <Kpi icon={<CalendarDays size={14} />} label="Total days" value={fmt(grand.days)} />
              <Kpi icon={<Clock size={14} />} label="Total hours" value={fmt(grand.hours)} />
              <Kpi icon={<Layers size={14} />} label="Subjects" value={String(subjects.length)} />
              <Kpi
                icon={<Activity size={14} />}
                label="Weeks active"
                value={`${weeksActive}/${weeks.length}`}
              />
            </div>

            {/* Legend */}
            <div className="flex items-center gap-2 px-4 sm:px-5 py-2 border-b text-[11px] text-gray-500">
              <span>Low</span>
              <div className="flex rounded overflow-hidden border">
                {legendStops.map((t) => (
                  <span
                    key={t}
                    className="w-6 h-3"
                    style={{
                      background:
                        t === 0
                          ? "#f3f4f6"
                          : `rgba(79,70,229,${(0.14 + 0.82 * t).toFixed(3)})`,
                    }}
                  />
                ))}
              </div>
              <span>High</span>
              <span className="hidden sm:flex items-center gap-1 ml-3 pl-3 border-l">
                <span
                  className="w-3 h-3 rounded-sm"
                  style={{ background: "rgba(217,119,6,0.85)" }}
                />
                row total
              </span>
              <span className="ml-auto capitalize">
                heat = {metric} logged{maxCell ? ` · peak ${fmt(maxCell)}` : ""}
              </span>
            </div>

            {/* Matrix */}
            <div className="flex-1 overflow-auto">
              {isLoading && (
                <p className="text-sm text-gray-500 p-6">Loading analysis…</p>
              )}
              {isError && (
                <p className="text-sm text-red-600 p-6">
                  Failed to load analysis sheet.
                </p>
              )}

              {!isLoading && !isError && (
                <table className="text-xs border-separate border-spacing-0 min-w-full">
                  <thead>
                    <tr>
                      <th className="sticky left-0 top-0 z-30 bg-gray-100 text-left font-semibold text-gray-600 px-3 py-2 border-b border-r min-w-[200px]">
                        Subject / Training Code
                      </th>
                      {weeks.map((w) => (
                        <th
                          key={w}
                          className="sticky top-0 z-20 bg-gray-100 font-semibold text-gray-500 px-2 py-2 border-b text-center min-w-[32px]"
                        >
                          {w}
                        </th>
                      ))}
                      <th className="sticky top-0 right-0 z-20 bg-indigo-100 font-bold text-indigo-800 px-3 py-2 border-b border-l text-center min-w-[52px]">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.length === 0 && (
                      <tr>
                        <td
                          colSpan={weeks.length + 2}
                          className="text-center text-gray-400 px-3 py-10"
                        >
                          No entries logged yet.
                        </td>
                      </tr>
                    )}
                    {subjects.map((s) => (
                      <tr key={s.id ?? "none"} className="group">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-indigo-50 px-3 py-2 border-b border-r">
                          <div className="font-semibold text-gray-800 leading-tight">
                            {s.code}
                          </div>
                          <div
                            title={s.description}
                            className="text-[10px] text-gray-500 leading-tight truncate max-w-[180px] cursor-help"
                          >
                            {s.description}
                          </div>
                        </td>
                        {weeks.map((w) => {
                          const c = s.cells?.[String(w)];
                          const v = metricOf(c);
                          return (
                            <td
                              key={w}
                              title={
                                c
                                  ? `${s.code} · Week ${w}: ${c.days} day(s), ${fmt(
                                      c.hours
                                    )} hr`
                                  : undefined
                              }
                              className="px-1 py-2 border-b text-center font-semibold"
                              style={heat(v)}
                            >
                              {v > 0 ? fmt(v) : ""}
                            </td>
                          );
                        })}
                        <td
                          className="px-3 py-2 border-b border-l text-center font-bold"
                          style={heatTotal(totalOf(s))}
                          title={`${s.code} total: ${s.total_days} day(s), ${fmt(
                            s.total_hours
                          )} hr`}
                        >
                          {metric === "days" ? s.total_days : fmt(s.total_hours)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="sticky left-0 bottom-0 z-20 bg-gray-100 px-3 py-2 border-t border-r font-bold text-gray-700">
                        Total ({metric})
                      </td>
                      {weeks.map((w) => {
                        const c = columnTotals?.[String(w)];
                        const v = metricOf(c);
                        return (
                          <td
                            key={w}
                            className="sticky bottom-0 z-10 bg-gray-100 px-1 py-2 border-t text-center font-semibold text-gray-700"
                          >
                            {v > 0 ? fmt(v) : "·"}
                          </td>
                        );
                      })}
                      <td className="sticky bottom-0 right-0 z-10 bg-indigo-200 px-3 py-2 border-t border-l text-center font-black text-indigo-900">
                        {metric === "days" ? grand.days : fmt(grand.hours)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Kpi({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-white rounded-lg border px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-400 uppercase">
        <span className="text-indigo-500">{icon}</span>
        {label}
      </div>
      <div className="text-lg font-black text-gray-800 leading-tight">{value}</div>
    </div>
  );
}
